import React, { useEffect, useState } from 'react';
import Big from 'big.js';

import IWithdrawConfirmation, { TWithdrawConfirmation } from './IWithdrawConfirmation';
import FundsService from '@services/Funds.service';
import { TReducers } from '@features/reducers';
import { connect } from 'react-redux';
import Loader from '@appComponents/Loader/Loader';
import CurrencyElement from '@appComponents/CurrencyElement/CurrencyElement';
import * as elements from '@appStyles/_elements.module.scss';
import { formatDate } from '@features/utils/functions';
import { classNames } from '@features/utils/reactFunctions';
import { AxiosResponse } from 'axios';
import { associatedRecord } from
  '@funds/components/TransactionHistoryTable/components/TransactionHistoryTableRow/ITransactionHistoryTableRow';
import * as style from './WithdrawConfirmationStyle.module.scss'

const WithdrawConfirmation: React.FunctionComponent<IWithdrawConfirmation> = (props) => {
  const [withdrawData, setWithdrawData] = useState<TWithdrawConfirmation | undefined>(undefined);

  useEffect(() => {
    if (process.env.ENABLE_BINANCE_TRADING) {
      FundsService.getWithdrawFromPath(props.withdrawPath).then((res) => {
        const { associated_records, ...rest } = res.data;
        setWithdrawData({
          ...rest,
          associatedRecord: associated_records.find(
            (el: associatedRecord) => el.type === 'Blocked'
          ),
        });
      });
    } else {
      Promise.all([
        FundsService.oldGetWithdrawsHistoryDetails(props.withdrawId),
        FundsService.getTransactionsHistoryDetails(props.withdrawId),
      ]).then((res: AxiosResponse[]) => {
        setWithdrawData({ ...res[0].data, associatedRecord: res[1].data.associated_records[0] });
      });
    }
  }, []);

  const renderRow = (label: string, value: string | JSX.Element) => {
    return (
      <div className={elements.zebraTableRow}>
        <div className={elements.zebraTableItem}>
          {label}
        </div>
        <div
          className={classNames(elements.zebraTableItem, label === 'Wallet address' && style.scrollable)}
        >
          {value}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (withdrawData) {
      const { balance_before, associatedRecord, created_at, amount, currency, accountable, investor_id } = withdrawData;

      return (
        <>
          <h3 className={style.withdrawConfirmationDescription}>
            Please review the following details of the withdrawal request issued from your account:
          </h3>
          <div className={classNames(elements.zebraTableContainer, style.zebraTableContainer)}>
            {amount && currency && renderRow(
              'Withdrawal amount',
              <CurrencyElement value={amount} currency={currency}/>
              )}
            {currency && renderRow(
              'BF Funds Balance after withdrawal',
              <CurrencyElement
                value={new Big(Number(balance_before)).minus(associatedRecord.amount)}
                currency={currency}
              />)
            }
            {typeof created_at === 'string' && renderRow('Issued at', formatDate(created_at))}
            {accountable && accountable.name && <>
              {renderRow('Wallet name', accountable.name)}
              {renderRow('Wallet currency', accountable.currency.toUpperCase())}
            </>}
            {accountable && accountable.address &&
              renderRow('Wallet address', accountable.address)}
            {accountable && (accountable.account_number || accountable.iban || accountable.aba) &&
              renderRow(
                'Destination account identifier',
                accountable.account_number || accountable.iban || accountable.aba!
              )
            }
          </div>
          {props.investorId === investor_id &&
            <h3 className={style.withdrawConfirmationDescription}>
              Full withdrawal details are shown in withdraw history
              of <a target="_blank" href={`/profile/${props.slug}/funds/manage/withdraw?currency=${currency}`}>BF Funds</a>.
            </h3>
          }
        </>
      );
    }

    return <Loader moreSpace={true}/>;
  };

  return (
    renderContent()
  );
};

const mapStateToProps = (store: TReducers) => ({
  investorId: store.userInfo.investor_id,
  slug: store.userInfo.nickname,
});

export default connect(mapStateToProps)(WithdrawConfirmation);
